import type { ReactNode } from "react";
import { checkedChannelNames, type ChannelSelectionTopic } from "@/api";
import { ChannelStructureGroups } from "@/components/dataset/ChannelStructureGroups";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { MUX_TOPICS } from "./recipe";
import { intersectDatasetStructures } from "./structureIntersection";
import type { SubmitWizardController } from "./useSubmitWizardController";
import styles from "./submitWizard.module.css";

// 채널 매핑 섹션이다. 선택한 데이터셋의 실제 채널(카메라·관측 벡터·액션)을 토글로 고른다.
// 여기서 고른 값은 제출 payload 의 channel_selection 이 된다. BE 는 config 의 policy.mux 를
// 이 선택으로 교체한다(override 주입).
// config 와 데이터셋의 채널 어휘가 달라도 이름과 관계없이 동작한다(예: 한팔 config × 양팔
// 데이터셋, wrist_upper/lower 카메라). 그래서 이 섹션은 config 가 기대하는 채널과의 차이를
// 표시하지 않고 데이터셋 채널만 표시한다. 팔 구성 요약도 두지 않는다. 토글 그룹이 곧 구조다.

const FIXED_CHANNEL_KEYS = new Set(["action/done"]);

export function ChannelSelectionEditor({
  controller,
}: {
  controller: SubmitWizardController;
}): ReactNode {
  const { form, structureState, toggleChannel } = controller;
  if (form.datasets.length === 0) {
    return null;
  }
  if (structureState.status === "loading" || structureState.status === "idle") {
    return <LoadingState size="sm">Dataset 채널 구조를 불러오고 있습니다.</LoadingState>;
  }
  if (structureState.status === "error") {
    // 구조를 받지 못해도 제출할 수 있다. 서버가 채널을 맞춘다. 제출을 막지 않는 실패라서 오류 tone 을
    // 쓰지 않는다(docs/rules.md §19).
    return (
      <EmptyState size="sm">
        채널 구조를 확인할 수 없습니다. 제출하면 서버가 Dataset에 맞게 자동으로 조정합니다.
      </EmptyState>
    );
  }
  const structures = structureState.data;
  const selection = form.channelSelection;
  // 학습이 N개를 한 배치로 섞으므로 고를 수 있는 채널은 구조들의 교집합뿐이다.
  const { channels: sharedChannels } = intersectDatasetStructures(structures);

  return (
    <div className={styles.section} aria-label="mux">
      {selection === null ? (
        <LoadingState size="sm">추천 채널을 계산하고 있습니다.</LoadingState>
      ) : null}
      <ChannelStructureGroups
        mode="selectable"
        channels={sharedChannels.filter((channel) => isSelectableTopic(channel.topic))}
        // 데이터셋이 여러 개면 "일부 N/M ep" 부분 커버리지 표기의 기준 데이터셋을 정할 수 없다.
        // 그래서 0 을 전달해 그 캡션을 끈다. 0 이면 ChannelStructureGroups 가 비교하지 않는다.
        episodeCount={structures.length === 1 ? structures[0].episode_count : 0}
        topicOrder={MUX_TOPICS}
        // 위임(null) topic 은 체크 없음으로 그린다. 무엇이 들어갈지는 BE 제안이 소유한다.
        selectedNames={selection === null ? {} : checkedChannelNames(selection)}
        fixedChannelKeys={FIXED_CHANNEL_KEYS}
        disabled={selection === null}
        onToggle={(topic, name) => {
          if (isSelectableTopic(topic)) {
            toggleChannel(topic, name);
          }
        }}
      />
    </div>
  );
}

function isSelectableTopic(topic: string): topic is ChannelSelectionTopic {
  return MUX_TOPICS.some((candidate) => candidate === topic);
}
